/** Platform Administration.
 *
 * Two rules from the platform standard shape this screen:
 *
 * * **Provider forms are rendered from registry metadata**, never hand-built per
 *   provider. A provider added to the registry gains its configuration page here with no
 *   UI work at all — which is the whole reason the registry publishes a schema.
 * * **Secrets are never displayed.** Fields marked secret render as write-only, and the
 *   API redacts them regardless of what this screen chooses to show.
 *
 * The tabs a person sees follow their permissions. That is usability, not enforcement —
 * every endpoint behind these screens checks the same permission independently.
 */

import { useState } from "react";
import {
  Terminal,
  Activity,
  FileSpreadsheet,
  FolderSync,
  KeyRound,
  ScrollText,
  Server,
  ToggleLeft,
  Users,
} from "lucide-react";

import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  SectionHeader,
  inputClass,
} from "@/components/ui";
import { Permissions, useSession, usePermission } from "@/lib/session";
import { AdoSyncTab } from "@/features/administration/AdoSyncTab";
import { ApiUsageTab } from "@/features/api-access/ApiUsageTab";
import { ProjectImportTab } from "@/features/administration/ProjectImportTab";
import {
  useAddExternalPerson,
  useAdminUsers,
  useAudit,
  useFeatureFlags,
  useProviders,
  useRoles,
  useSetActivation,
  useSystemHealth,
  useTestProvider,
  useUpdateExternalPerson,
  type AdminUser,
  type ConfigField,
  type ProviderDescriptor,
} from "@/features/administration/useAdmin";

const statusTone = (status: string) =>
  status === "healthy" ? "green" : status === "degraded" ? "amber" : "red";

// --- health ---------------------------------------------------------------

function HealthTab() {
  const { data, isPending, error } = useSystemHealth();
  if (isPending) return <Loading label="Checking dependencies" />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const checks = Object.entries(data.checks);

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <Pill tone={statusTone(data.status)}>{data.status}</Pill>
        <span className="text-sm text-fg-muted">
          {checks.length} dependencies · refreshed every 30 seconds
        </span>
      </div>
      <ul className="card divide-y divide-border">
        {checks.map(([name, check]) => (
          <li
            key={name}
            className="flex flex-wrap items-center gap-3 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium text-fg">{name}</div>
              <div className="truncate text-xs text-fg-muted">
                {check.detail ?? "—"}
              </div>
            </div>
            {check.latencyMs !== null ? (
              <span className="text-sm text-fg-muted">
                {Math.round(check.latencyMs)} ms
              </span>
            ) : null}
            <Pill tone={statusTone(check.status)}>{check.status}</Pill>
          </li>
        ))}
      </ul>
    </>
  );
}

// --- providers ------------------------------------------------------------

/** One configuration field, rendered from the registry's schema. */
function SchemaField({ field }: { field: ConfigField }) {
  return (
    <Field
      label={`${field.label}${field.required ? " *" : ""}`}
      hint={field.description || undefined}
    >
      {field.secret ? (
        <input
          className={inputClass}
          type="password"
          value=""
          readOnly
          placeholder="Write-only — set, never read back"
        />
      ) : field.choices.length > 0 ? (
        <select
          className={inputClass}
          defaultValue={field.default ?? ""}
          disabled
        >
          {field.choices.map((choice) => (
            <option key={choice}>{choice}</option>
          ))}
        </select>
      ) : (
        <input
          className={inputClass}
          defaultValue={field.default ?? ""}
          placeholder={field.type}
          disabled
        />
      )}
    </Field>
  );
}

function ProviderCard({
  descriptor,
  bindings,
}: {
  descriptor: ProviderDescriptor;
  bindings: string[];
}) {
  const [open, setOpen] = useState(false);
  const test = useTestProvider();

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-fg">
            {descriptor.displayName}{" "}
            <span className="text-xs font-normal text-fg-muted">
              v{descriptor.version}
            </span>
          </div>
          <div className="text-xs text-fg-muted">
            {descriptor.category} · {descriptor.providerId}
          </div>
        </div>
        {bindings.length > 0 ? (
          bindings.map((binding) => (
            <Pill key={binding} tone="green">
              {binding}
            </Pill>
          ))
        ) : (
          <Pill>Not bound</Pill>
        )}
        <Button onClick={() => setOpen((value) => !value)}>
          {open ? "Hide" : "Configuration"}
        </Button>
        {bindings.length > 0 ? (
          <Button
            onClick={() => test.mutate(bindings[0] ?? "")}
            disabled={test.isPending}
          >
            {test.isPending ? "Testing…" : "Test"}
          </Button>
        ) : null}
      </div>

      <p className="mt-1 text-sm text-fg-muted">{descriptor.description}</p>

      {test.data ? (
        <p
          className="mt-2 text-sm"
          style={{ color: `var(--status-${statusTone(test.data.status)})` }}
        >
          {test.data.status} — {test.data.detail ?? "no detail"}
          {test.data.latencyMs !== null
            ? ` (${Math.round(test.data.latencyMs)} ms)`
            : ""}
        </p>
      ) : null}
      {test.isError ? (
        <div className="mt-2">
          <ErrorState error={test.error} />
        </div>
      ) : null}

      {open ? (
        <div className="mt-3 border-t border-border pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {descriptor.configSchema.map((field) => (
              <SchemaField key={field.name} field={field} />
            ))}
          </div>
          {descriptor.configSchema.length === 0 ? (
            <p className="text-sm text-fg-muted">
              This provider takes no configuration.
            </p>
          ) : null}
          {/* Honest about where these values actually come from today. */}
          <p className="mt-3 text-xs text-fg-muted">
            Rendered from the provider registry, so a new provider gains this
            form without UI work. Values are bound at startup from application
            settings and Key Vault and are read-only here — editing them in
            place is the next step, and until then a change means a
            configuration update and a restart.
          </p>
        </div>
      ) : null}
    </li>
  );
}

function ProvidersTab() {
  const { data, isPending, error } = useProviders();
  if (isPending) return <Loading label="Loading the provider registry" />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const bindingsFor = (providerId: string) =>
    data.active
      .filter((entry) => entry.providerId === providerId)
      .map((entry) => entry.binding);

  return (
    <ul className="card divide-y divide-border">
      {data.available.map((descriptor) => (
        <ProviderCard
          key={`${descriptor.category}:${descriptor.providerId}`}
          descriptor={descriptor}
          bindings={bindingsFor(descriptor.providerId)}
        />
      ))}
    </ul>
  );
}

// --- users and roles ------------------------------------------------------

/** Third-party people: a vendor's staff who do project work but never sign in.
 *
 * On this tab because "who can be given work" is the question it answers, even though
 * these are not accounts. Keeping them on a page of their own would separate two halves of
 * one question.
 *
 * Retiring, not deleting. Somebody taken off the list keeps their name on work they did;
 * they simply stop appearing in the pickers.
 */
function ThirdPartyPeople({ people }: { people: AdminUser[] }) {
  const add = useAddExternalPerson();
  const update = useUpdateExternalPerson();
  const canEdit = usePermission(Permissions.adminUsersEdit);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    display_name: "",
    organisation: "",
    email: "",
  });

  const submit = () => {
    if (
      !form.display_name.trim() ||
      !form.organisation.trim() ||
      !form.email.includes("@")
    )
      return;
    add.mutate(
      {
        display_name: form.display_name.trim(),
        organisation: form.organisation.trim(),
        email: form.email.trim(),
      },
      {
        onSuccess: () => {
          setForm({ display_name: "", organisation: "", email: "" });
          setAdding(false);
        },
      },
    );
  };

  return (
    <section className="mt-8">
      <SectionHeader
        action={
          canEdit ? (
            <button
              type="button"
              className="text-xs underline text-fg-muted hover:text-fg"
              onClick={() => setAdding((open) => !open)}
            >
              Add a person
            </button>
          ) : undefined
        }
      >
        Third-party people
      </SectionHeader>
      <p className="mb-3 max-w-[70ch] text-xs text-fg-muted">
        Vendors and contractors who can be assigned to projects. They cannot
        sign in and hold no permissions — this is a name to put against work,
        not an account. An email is required so whoever picks up the project can
        reach them.
      </p>

      {adding ? (
        <div className="card mb-3 flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-[180px] flex-1">
            <Field label="Name">
              <input
                className={inputClass}
                value={form.display_name}
                autoFocus
                onChange={(e) =>
                  setForm({ ...form, display_name: e.target.value })
                }
              />
            </Field>
          </div>
          <div className="min-w-[160px] flex-1">
            <Field label="Organisation">
              <input
                className={inputClass}
                value={form.organisation}
                placeholder="e.g. NetCuras"
                onChange={(e) =>
                  setForm({ ...form, organisation: e.target.value })
                }
              />
            </Field>
          </div>
          <div className="min-w-[180px] flex-1">
            <Field label="Email" hint="Required — how the team reaches them.">
              <input
                className={inputClass}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
          </div>
          <Button
            variant="primary"
            disabled={
              add.isPending ||
              !form.display_name.trim() ||
              !form.organisation.trim() ||
              !form.email.includes("@")
            }
            onClick={submit}
          >
            {add.isPending ? "Adding…" : "Add"}
          </Button>
          <Button variant="secondary" onClick={() => setAdding(false)}>
            Cancel
          </Button>
          {add.error ? (
            <div className="w-full">
              <ErrorState error={add.error} />
            </div>
          ) : null}
        </div>
      ) : null}

      {people.length === 0 ? (
        <p className="card px-4 py-4 text-sm text-fg-muted">
          Nobody yet. Add a vendor&rsquo;s people here and they become
          assignable to projects.
        </p>
      ) : (
        <ul className="card divide-y divide-border">
          {people.map((person) => (
            <li
              key={person.id}
              className="flex flex-wrap items-center gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-fg">
                  {person.displayName}
                </div>
                <div className="truncate text-xs text-fg-muted">
                  {person.organisation ?? "No organisation"}
                  {person.email ? ` · ${person.email}` : ""}
                </div>
              </div>
              {person.isActive ? null : <Pill>Retired</Pill>}
              {canEdit ? (
                <Button
                  variant="secondary"
                  disabled={update.isPending}
                  title={
                    person.isActive
                      ? "Take them out of the pickers. Past assignments keep their name."
                      : "Bring them back into the pickers."
                  }
                  onClick={() =>
                    update.mutate({
                      id: person.id,
                      is_active: !person.isActive,
                    })
                  }
                >
                  {person.isActive ? "Retire" : "Restore"}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {update.error ? <ErrorState error={update.error} /> : null}
    </section>
  );
}

function UsersTab() {
  const { data, isPending, error } = useAdminUsers();
  const { data: roles } = useRoles();
  const setActivation = useSetActivation();
  const canEdit = usePermission(Permissions.adminUsersEdit);

  if (isPending) return <Loading label="Loading users" />;
  if (error) return <ErrorState error={error} />;

  // Directory accounts and third-party people answer different questions and are listed
  // separately: one is 'who can sign in and with what role', the other 'who can be given
  // work'. Mixed together, a vendor with no roles reads as a misconfigured employee.
  const accounts = (data ?? []).filter((u) => u.principalType !== "external");
  const external = (data ?? []).filter((u) => u.principalType === "external");

  return (
    <>
      <ul className="card divide-y divide-border">
        {accounts.map((user) => (
          <li
            key={user.id}
            className="flex flex-wrap items-center gap-3 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-fg">
                {user.displayName}
              </div>
              <div className="truncate text-xs text-fg-muted">
                {user.email} ·{" "}
                {user.lastLoginAt
                  ? `last signed in ${new Date(user.lastLoginAt).toLocaleString()}`
                  : "never signed in"}
              </div>
            </div>
            {user.roles.map((role) => (
              <Pill key={role} tone="info">
                {role}
              </Pill>
            ))}
            {user.isActive ? (
              <Pill tone="green">Active</Pill>
            ) : (
              <Pill tone="red">Deactivated</Pill>
            )}
            {canEdit ? (
              <Button
                onClick={() =>
                  setActivation.mutate({
                    id: user.id,
                    isActive: !user.isActive,
                  })
                }
                disabled={setActivation.isPending}
              >
                {user.isActive ? "Deactivate" : "Reactivate"}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-fg-muted">
        Roles come from the Entra <code>roles</code> claim and are reconciled at
        every sign-in. Deactivating here overrides the directory and takes
        effect on the next request.
      </p>

      <div className="mt-8">
        <SectionHeader>Roles</SectionHeader>
      </div>
      <ul className="card divide-y divide-border">
        {(roles ?? []).map((role) => (
          <li key={role.id} className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-fg">{role.name}</div>
                <div className="text-xs text-fg-muted">{role.description}</div>
              </div>
              <span className="text-sm text-fg-muted">
                {role.permissions.length} permissions
              </span>
              {role.isSystem ? <Pill>System</Pill> : null}
            </div>
          </li>
        ))}
      </ul>

      <ThirdPartyPeople people={external} />
    </>
  );
}

// --- feature flags --------------------------------------------------------

function FlagsTab() {
  const { data, isPending, error } = useFeatureFlags();
  if (isPending) return <Loading label="Loading feature flags" />;
  if (error) return <ErrorState error={error} />;

  const flags = data ?? [];
  if (flags.length === 0) {
    return (
      <EmptyState title="No feature flags defined">
        Flags are evaluated server-side and the client reads the resolved state,
        so adding one never means shipping rule logic to the browser.
      </EmptyState>
    );
  }

  return (
    <ul className="card divide-y divide-border">
      {flags.map((flag) => (
        <li key={flag.key} className="flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-fg">{flag.key}</div>
            <div className="text-xs text-fg-muted">{flag.description}</div>
          </div>
          <Pill tone={flag.isEnabled ? "green" : "neutral"}>
            {flag.isEnabled ? "On" : "Off"}
          </Pill>
        </li>
      ))}
    </ul>
  );
}

// --- audit ----------------------------------------------------------------

function AuditTab() {
  const [action, setAction] = useState("");
  const [resourceType, setResourceType] = useState("");
  const { data, isPending, error } = useAudit({
    action,
    resource_type: resourceType,
  });

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className={`${inputClass} max-w-[220px]`}
          value={action}
          onChange={(event) => setAction(event.target.value)}
          placeholder="Action, e.g. access_denied"
        />
        <input
          className={`${inputClass} max-w-[220px]`}
          value={resourceType}
          onChange={(event) => setResourceType(event.target.value)}
          placeholder="Resource type, e.g. Project"
        />
      </div>

      {isPending ? <Loading label="Searching the audit trail" /> : null}
      {error ? <ErrorState error={error} /> : null}

      {data && data.length === 0 ? (
        <EmptyState title="No matching events" />
      ) : null}

      {data && data.length > 0 ? (
        <ul className="card divide-y divide-border">
          {data.map((event) => (
            <li key={event.id} className="px-4 py-2.5 text-sm">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium text-fg">{event.action}</span>
                {event.outcome !== "success" ? (
                  <Pill tone={event.outcome === "denied" ? "amber" : "red"}>
                    {event.outcome}
                  </Pill>
                ) : null}
                <span className="text-fg-muted">
                  {event.resourceType}
                  {event.resourceId ? ` · ${event.resourceId}` : ""}
                </span>
                <span className="ml-auto text-xs text-fg-muted">
                  {new Date(event.occurredAt).toLocaleString()} · {event.actor}
                </span>
              </div>
              {event.reason ? (
                <div className="text-xs text-fg-muted">{event.reason}</div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mt-3 text-xs text-fg-muted">
        Append-only: the database rejects updates and deletes on this table, so
        what is here is what happened. Denied and failed attempts are included —
        they are most of the point.
      </p>
    </>
  );
}

// --- shell ----------------------------------------------------------------

const TABS = [
  {
    key: "health",
    label: "Health",
    icon: Activity,
    permission: Permissions.adminHealthView,
  },
  {
    key: "providers",
    label: "Providers",
    icon: Server,
    permission: Permissions.adminProvidersView,
  },
  {
    key: "users",
    label: "Users & roles",
    icon: Users,
    permission: Permissions.adminUsersView,
  },
  {
    key: "flags",
    label: "Feature flags",
    icon: ToggleLeft,
    permission: Permissions.adminFlagsEdit,
  },
  {
    key: "audit",
    label: "Audit trail",
    icon: ScrollText,
    permission: Permissions.adminAuditView,
  },
  // Bulk-loading a portfolio: download the template, fill it out, upload, preview,
  // apply. Gated on admin.access, which its API demands, now that people who are not
  // administrators can open this page for API access: a tab that answers 403 when used
  // is worse than no tab.
  {
    key: "import",
    label: "Import projects",
    icon: FileSpreadsheet,
    permission: Permissions.adminAccess,
  },
  // Which Azure DevOps epics mirror into the portfolio, and where they file.
  {
    key: "ado",
    label: "ADO Sync",
    icon: FolderSync,
    permission: Permissions.adminSyncView,
  },
  // API Administration: consumers, volume, errors, rate limiting, recent failures. How to
  // call the API moved to its own page, /api-access, open to everyone (2026-10-29).
  {
    key: "api",
    label: "API usage",
    icon: Terminal,
    permission: Permissions.adminAccess,
  },
] as const;

export function AdministrationPage() {
  // Read the permission set once. Calling `usePermission` per tab would be a hook in a
  // loop, which breaks the moment the tab list becomes conditional.
  const { data: session } = useSession();
  const held = session?.permissions ?? [];
  const visible = TABS.filter((tab) => held.includes(tab.permission));
  const [active, setActive] = useState<string>("health");

  if (visible.length === 0) {
    return (
      <div className="mx-auto max-w-[900px] pt-2">
        <EmptyState title="Nothing here for you">
          Platform Administration is available, but none of its areas are open
          to your role.
        </EmptyState>
      </div>
    );
  }

  const current = visible.find((tab) => tab.key === active) ?? visible[0];
  if (!current) return null;

  return (
    <div className="mx-auto max-w-[1100px] pt-2">
      {/* Someone who is not an administrator reaches this page only for API access, so it is
          titled for what they came for rather than for an area they cannot see. */}
              <header className="mb-6">
          <h1 className="font-heading text-3xl text-fg">
            Platform Administration
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            How this deployment is configured and how it is behaving. Container
            and infrastructure operations live in Azure; this answers the
            application questions.
          </p>
        </header>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {visible.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.key === current?.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActive(tab.key)}
              className="-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold"
              style={{
                borderColor: isActive ? "var(--brand)" : "transparent",
                color: isActive ? "var(--fg)" : "var(--fg-muted)",
              }}
            >
              <Icon size={15} aria-hidden />
              {tab.label}
            </button>
          );
        })}
      </div>

      {current?.key === "health" ? <HealthTab /> : null}
      {current?.key === "providers" ? <ProvidersTab /> : null}
      {current?.key === "users" ? <UsersTab /> : null}
      {current?.key === "flags" ? <FlagsTab /> : null}
      {current?.key === "audit" ? <AuditTab /> : null}
      {current?.key === "import" ? <ProjectImportTab /> : null}
      {current?.key === "ado" ? <AdoSyncTab /> : null}
      {current?.key === "api" ? <ApiUsageTab /> : null}

      <div className="mt-10 flex items-center gap-2 text-xs text-fg-muted">
        <KeyRound size={13} aria-hidden />
        Secrets are write-only everywhere in this area. The API redacts them
        before they leave the server, so there is no path that returns one to a
        browser.
      </div>
    </div>
  );
}
